import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getUnitsForItems,
  type ItemUnitOptions,
  type UnitOption,
} from '../services/inventoryItemUnitService';

const CHUNK = 500;

function baseOnly(unit?: string | null): ItemUnitOptions {
  const name = (unit ?? '').trim();
  const options: UnitOption[] = [{
    unitName: name,
    factor: 1,
    isBase: true,
    isDefaultForProduction: false,
  }];
  return { baseUnit: name, options };
}

/**
 * Bileşen ömrü boyunca birim seçeneklerini önbelleğe alır.
 * Yalnız eksik kimlikler, 500'lük parçalar halinde istenir.
 * Üretim modülü kapalıysa istek atılmaz; satırdaki ana birim döner.
 */
export function useItemUnitOptions(
  itemIds: string[],
  baseUnits: Record<string, string | null | undefined> = {},
): Record<string, ItemUnitOptions> {
  const { isModuleEnabled } = useAuth();
  const enabled = isModuleEnabled('production');
  const cache = useRef(new Map<string, ItemUnitOptions>());
  const baseUnitsRef = useRef(baseUnits);
  baseUnitsRef.current = baseUnits;
  const [version, setVersion] = useState(0);
  const idsKey = [...new Set(itemIds.filter((id) => !!id))].join('\n');

  useEffect(() => {
    if (!enabled) return undefined;
    const missing = idsKey ? idsKey.split('\n').filter((id) => !cache.current.has(id)) : [];
    if (missing.length === 0) return undefined;
    let cancelled = false;

    const load = async () => {
      for (let offset = 0; offset < missing.length; offset += CHUNK) {
        const chunk = missing.slice(offset, offset + CHUNK);
        try {
          const found = await getUnitsForItems(chunk);
          if (cancelled) return;
          for (const id of chunk) {
            cache.current.set(
              id,
              found[id.toLowerCase()] ?? baseOnly(baseUnitsRef.current[id]),
            );
          }
        } catch {
          if (cancelled) return;
        }
        if (!cancelled) setVersion((value) => value + 1);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey]);

  const result: Record<string, ItemUnitOptions> = {};
  const ids = idsKey ? idsKey.split('\n') : [];
  for (const id of ids) {
    if (!enabled) {
      result[id] = baseOnly(baseUnits[id]);
      continue;
    }
    result[id] = (version >= 0 ? cache.current.get(id) : undefined) ?? baseOnly(baseUnits[id]);
  }
  return result;
}
